import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { FormElement } from '@app/modules/form-generator/edit-form-v3/models/form.model';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { TranslateService } from '@ngx-translate/core';

// Shared behaviour of the localization fields (app-localization-text-form-field and
// app-localization-text-area-form-field). Each renders a { culture, <value> }[] form array: the first row
// is always shown and the other translations sit behind a toggle.
@Directive()
export abstract class LocalizationFieldBase {
  @Input() formElement: FormElement;
  @Input() parentGroup: UntypedFormGroup;
  @Input() readOnly: boolean;
  @Input() currentCulture: Culture;
  @Input() cultures: Culture[] = [];
  // Hide the heading when the host already labels the field
  @Input() showTitle: boolean = true;
  // Only offer languages that no other row uses, and hide "add" once every language has a row
  @Input() hideUsedCultures: boolean = false;

  @Output() emitAddtoFormArray: EventEmitter<string> = new EventEmitter<string>();
  @Output() emitDeleteFromFormArray: EventEmitter<{index: number, formControl: string}> = new EventEmitter<{index: number, formControl: string}>();

  // Text area rather than a single line input
  multiline = false;
  isExpanded = false;

  constructor(
    public translate: TranslateService
  ) { }

  get rows(): AbstractControl[] {
    return (this.parentGroup?.get(this.formElement?.formControl) as UntypedFormArray)?.controls ?? [];
  }

  get editable(): boolean {
    return this.formElement?.disabled !== 'true' && !this.readOnly;
  }

  flagSrc(cultureId: string): string {
    if (!cultureId) {
      return null;
    }

    return this.isSpecialUrl() && this.isEnGbCulture(cultureId)
      ? 'assets/icons/world-icon.png'
      : `assets/country-flags/svg/${cultureId}.svg`;
  }

  isSpecialUrl(): boolean {
    const url = window.location.href;
    return url.includes('qa.hubbubhr.com') || url.includes('sightsavers-sandbox.hubbubhr.com') || url.includes('sightsavers.hubbubhr.com');
  }

  isEnGbCulture(culture: string | any): boolean {
    return culture === 'en-GB' || culture?.id === 'en-GB';
  }

  isCultureUsedElsewhere(cultureId: string, rowIndex: number): boolean {
    if (!this.hideUsedCultures) {
      return false;
    }

    return this.rows.some((row, index) => index !== rowIndex && row.get('culture')?.value === cultureId);
  }

  canAddLocalization(): boolean {
    return this.editable && (!this.hideUsedCultures || this.rows.length < this.cultures.length);
  }

  addNewLocalization() {
    this.emitAddtoFormArray.emit(this.formElement.formControl);
    this.isExpanded = true;
  }

  deleteLocalization(index: number) {
    this.emitDeleteFromFormArray.emit(
      {
        index: index,
        formControl: this.formElement.formControl
      }
    );
  }

  toggleDiv() {
    this.isExpanded = !this.isExpanded;
  }

  isFieldDisabled(): boolean {
    return this.formElement?.disabled === 'true' || this.readOnly;
  }

  // Announced to screen readers alongside the field, since disabled inputs give no reason
  getDisabledReadOnlyAnnouncement(): string {
    if (!this.isFieldDisabled()) {
      return '';
    }

    if (this.readOnly) {
      return this.translate.instant('Read-only', { Default: 'Read-only' });
    }

    if (this.formElement?.disabled === 'true') {
      return this.translate.instant('Disabled', { Default: 'Disabled' });
    }

    return '';
  }
}
